// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import type { DropdownOption } from '@loa/ui-components';
import { quoteApi, type ProductCatalogueEntry } from '../api/quoteApi';

interface UseProductCatalogueReturn {
  entries: ProductCatalogueEntry[];
  /** Ready-to-use dropdown options ({@code value} = product code, {@code text} = name). */
  options: DropdownOption[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads the Product Catalogue for a category (PENSION / INVESTMENT / INSURANCE) —
 * the single source of truth for the catalogue-driven quote type dropdowns. The
 * selected value is a product {@code code}, which the terms engine uses to
 * constrain the quote's option fan-out to that product.
 */
export function useProductCatalogue(category: string): UseProductCatalogueReturn {
  const [entries, setEntries] = useState<ProductCatalogueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    quoteApi
      .getProductCatalogue(category)
      .then((data) => {
        if (!cancelled) setEntries(data);
      })
      .catch((err) => {
        console.error('Failed to load product catalogue', err);
        if (!cancelled) setError('Failed to load product types');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  const options = useMemo<DropdownOption[]>(
    () => entries.map((e) => ({ value: e.code, text: e.name })),
    [entries],
  );

  return { entries, options, loading, error };
}
